import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { motion } from "motion/react"
import { type FC, useRef, useState } from "react"
import { Navigate, useNavigate } from "react-router"

import { academyNameTrademarkNotice, getAcademyNameValidationError } from "@forento/core/utilities/academyName"
import { getPasswordValidationError, passwordRequirementsText } from "@forento/core/utilities/password"
import Form from "@forento/ui/components/Form"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import InputField from "~/components/InputField"
import Link from "~/components/Link"
import { useBrand } from "~/contexts/BrandContext"
import { useDisableGoogleSignIn } from "~/contexts/GoogleSignInContext"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

import Layout, { Logo } from "../Layout"
import { authCardClass, staggerContainer, staggerItem } from "../Layout/authCard"

const ProductCanyonSignupPage: FC = () => {
	const brand = useBrand()
	const user = useUser()
	const navigate = useNavigate()
	useDisableGoogleSignIn()

	const turnstileRef = useRef<TurnstileInstance | null>(null)
	const [code, setCode] = useState("")
	const [validatedCode, setValidatedCode] = useState<{ key: string; label: string } | null>(null)
	const [isValidatingCode, setValidatingCode] = useState(false)
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [passwordConfirmation, setPasswordConfirmation] = useState("")
	const [platformName, setPlatformName] = useState("")
	const [isAccountEmailChecked, setAccountEmailChecked] = useState(true)
	const [contactEmail, setContactEmail] = useState("")

	const [error, setError] = useState<string>()
	const [isSubmitting, setSubmitting] = useState(false)

	const handleValidateCode = async () => {
		if (isValidatingCode) return
		setError(undefined)
		if (code.trim().length < 3 || code.trim().length > 150) {
			setError("Enter your Product Canyon redemption code (3–150 characters).")
			return
		}
		setValidatingCode(true)
		try {
			const result = await trpc.user.getProductCanyonKey.query(code.trim())
			if (result === null) {
				setError("That code is invalid or has already been redeemed.")
				return
			}
			setValidatedCode(result)
		} catch {
			setError("We couldn't validate your code. Please try again.")
		} finally {
			setValidatingCode(false)
		}
	}

	const handleSignup = async () => {
		if (!validatedCode || isSubmitting) return
		const captchaToken = turnstileRef.current?.getResponse()
		if (!captchaToken) {
			setError("Please complete the captcha verification.")
			return
		}
		setSubmitting(true)
		setError(undefined)
		if (password !== passwordConfirmation) {
			setError("Passwords don't match.")
			setSubmitting(false)
			return
		}
		const passwordError = getPasswordValidationError(password)
		if (passwordError !== null) {
			setError(passwordError)
			setSubmitting(false)
			return
		}
		const body = {
			firstName: firstName.trim(),
			lastName: lastName.trim(),
			email: email.trim(),
			password,
			platformName: platformName.trim(),
			contactEmail: isAccountEmailChecked ? email.trim() : contactEmail.trim(),
			productCanyonKey: validatedCode.key,
			captchaToken,
		}
		if ([body.firstName, body.lastName, body.password, body.email, body.platformName].some(x => x.length === 0)) {
			setError("Please fill in all fields.")
			setSubmitting(false)
			return
		}
		const academyNameError = getAcademyNameValidationError(body.platformName)
		if (academyNameError !== null) {
			setError(academyNameError)
			setSubmitting(false)
			return
		}
		try {
			const response = await trpc.user.registerCreatorProductCanyon.mutate(body)
			if (response.status === "invalid-key") {
				setValidatedCode(null)
				setError("That code is invalid or has already been redeemed.")
				setSubmitting(false)
				turnstileRef.current?.reset()
				return
			}
			if (response.status === "email-taken") {
				setError("That email address is already associated with an account.")
				setSubmitting(false)
				turnstileRef.current?.reset()
				return
			}
			await user.reload({ clearSessionCache: true })
			navigate(routes.account.createPlatform())
		} catch {
			setError("Something went wrong. Please try again later.")
			setSubmitting(false)
			turnstileRef.current?.reset()
		}
	}

	if (brand.id !== "forento") return <Navigate replace to={routes.account.signup()} />

	if (!validatedCode)
		return (
			<Layout>
				<Logo />
				<div className={authCardClass}>
					<Form onSubmit={handleValidateCode}>
						<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
							Redeem your Product Canyon deal
						</h1>
						<p className="mt-1.5 text-left text-sm text-zinc-500">
							Enter your code to validate your lifetime plan. Then create your account.
						</p>
						<div className="mt-6">
							<InputField
								label="Product Canyon code"
								value={code}
								onChange={setCode}
								disabled={isValidatingCode}
								placeholder="Enter your redemption code"
							/>
						</div>
						{error && (
							<p role="alert" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
								{error}
							</p>
						)}
						<SubmitButton
							variant="primary"
							isLoading={isValidatingCode}
							className="mt-5 w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
						>
							Validate code
						</SubmitButton>
						<p className="mt-5 text-center text-sm text-zinc-500">
							Already have an account?{" "}
							<Link to={routes.account.signin()} className="text-primary font-medium">
								Sign in
							</Link>
						</p>
					</Form>
				</div>
			</Layout>
		)

	return (
		<Layout>
			<Logo />
			<div className={authCardClass}>
				<Form onSubmit={handleSignup}>
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Create your account
							</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">
								Your {validatedCode.label} lifetime plan code is valid. Complete your details below.
							</p>
						</motion.div>

						<motion.div
							variants={staggerItem}
							className="mt-6 flex items-center justify-between gap-3 rounded-xl bg-zinc-50 px-4 py-3"
						>
							<p className="min-w-0 text-sm break-all text-zinc-700">
								<span className="block font-medium">{validatedCode.label} · Code validated</span>
								<code>{validatedCode.key}</code>
							</p>
							<Button
								isDisabled={isSubmitting}
								onClick={() => {
									setValidatedCode(null)
									setError(undefined)
								}}
							>
								Change code
							</Button>
						</motion.div>
						{error !== undefined && (
							<motion.p
								variants={staggerItem}
								className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700"
							>
								{error}
							</motion.p>
						)}

						<motion.div variants={staggerItem} className="mt-6 grid grid-cols-2 gap-3">
							<InputField
								label="First name"
								value={firstName}
								onChange={setFirstName}
								autoComplete="given-name"
							/>
							<InputField
								label="Last name"
								value={lastName}
								onChange={setLastName}
								autoComplete="family-name"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="email"
								label="Email"
								value={email}
								onChange={setEmail}
								autoComplete="email"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="password"
								label="Password"
								value={password}
								onChange={setPassword}
								autoComplete="new-password"
							/>
							<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
								{passwordRequirementsText}
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField
								inputType="password"
								label="Confirm password"
								value={passwordConfirmation}
								onChange={setPasswordConfirmation}
								autoComplete="new-password"
							/>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<InputField label="Academy name" value={platformName} onChange={setPlatformName} />
							<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
								{academyNameTrademarkNotice}
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<Checkbox
								label="Use my account's email address"
								isChecked={isAccountEmailChecked}
								onChange={setAccountEmailChecked}
								className="mb-2"
							/>
							{isAccountEmailChecked ? (
								<InputField label="Contact email address" value={email} disabled />
							) : (
								<InputField
									label="Contact email address"
									placeholder="e.g. contact@davescourses.com"
									value={contactEmail}
									onChange={setContactEmail}
								/>
							)}
							<p className="mt-2 text-xs leading-relaxed text-zinc-500">
								The contact email will be public to your users and used in your terms of service.
							</p>
						</motion.div>

						<motion.div variants={staggerItem} className="mt-4">
							<Turnstile
								ref={turnstileRef}
								siteKey={CONFIG.turnstileSiteKey}
								className="flex justify-center"
							/>
						</motion.div>

						<motion.p
							variants={staggerItem}
							className="mt-5 text-left text-xs leading-relaxed text-zinc-500"
						>
							By signing up you agree to our{" "}
							<a
								href="https://forento.io/policy/terms-of-service"
								target="_blank"
								rel="noreferrer noopener"
								className="text-zinc-600 hover:text-zinc-900"
							>
								Terms of Service
							</a>
							.
						</motion.p>

						<motion.div variants={staggerItem} className="mt-5">
							<SubmitButton
								variant="primary"
								isLoading={isSubmitting}
								className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
							>
								Activate lifetime account
							</SubmitButton>
						</motion.div>

						<motion.p variants={staggerItem} className="mt-5 text-center text-sm text-zinc-500">
							Already have an account?{" "}
							<Link
								to={routes.account.signin()}
								className="text-primary hover:text-primary/80 font-medium transition-colors"
							>
								Sign in
							</Link>
						</motion.p>
					</motion.div>
				</Form>
			</div>
		</Layout>
	)
}

export default ProductCanyonSignupPage
